/*
 * Copyright (c) 2022-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {EsciComponent} from '../esci.component';
import {interval, Subject, timer} from 'rxjs';
import {switchMap, takeUntil} from 'rxjs/operators';
import {CommonHelperService} from '../../service/common-helper.service';
import {CacheService} from '../../service/cache-service.service';
import {environment} from '../../../environments/environment';

/**
 * Capital Component Usage page (Angular replacement for CapitalComponentUsageReportPage.jsp).
 *
 * Always queries Capital (source selection removed). Type-specific form fields with
 * single-value input per query. Uses an async job pattern (submit → poll → download)
 * to support Capital queries that can take up to 20 minutes, bypassing any proxy or
 * Tomcat HTTP timeout.
 */
@Component({
    selector: 'app-capital-component-usage',
    templateUrl: './capital-component-usage.component.html',
    standalone: false
})
export class CapitalComponentUsageComponent extends EsciComponent implements OnInit, OnDestroy {

    private cacheService = inject(CacheService);
    permissionUrl = '/esciUI/capitalComponentUsagePermission';
    private objectCodesLoaded = false;

    readonly MAX_POLL_MINUTES = 20;
    readonly POLL_INTERVAL_MS = 5_000;

    partTypes = [
        {label: 'Object Code / Object Code Instance', value: 'OCOCI'},
        {label: 'Connector (Part Number)', value: 'PN'},
        {label: 'Circuit', value: 'CIRCNO'},
    ];

    selectedType: string = '';

    // OCOCI fields
    selectedObjectCode: any = null;
    allObjectCodes: any[] = [];
    filteredObjectCodes: any[] = [];
    objectCodeInstance: string = '';

    // PN field
    partNumber: string = '';

    // CIRCNO field
    circuitNumber: string = '';

    // Runtime state
    isRunning: boolean = false;

    private stopPolling$ = new Subject<void>();
    private pollingDone = false;

    ngOnInit(): void {
        this.permission.set(false);
        this.esciService.getPermissionAndFileSizeLimit(this, false);
    }

    // -------------------------------------------------------------------------
    // Object Code pre-load (runs once on init, cached 30 min by CacheService)
    // -------------------------------------------------------------------------

    private loadAllObjectCodes(): void {
        if (this.objectCodesLoaded || !this.permission()) {
            return;
        }
        this.cacheService.getCacheable(
            `${environment.apiUrl}${this.esciService.objectCodeListUrl}`,
            {}
        ).subscribe({
            next: (result: any) => {
                const items = result?.body ?? [];
                this.allObjectCodes = items.map((oc: any) => ({
                    ...oc,
                    displayLabel: oc.objectCodeDesc
                        ? `${oc.code} – ${oc.objectCodeDesc}`
                        : oc.code,
                }));
                this.objectCodesLoaded = true;
            },
            error: () => {
                this.messageService.processError(
                    'Could not load Object Code list. Object Code search may be unavailable.'
                );
            }
        });
    }

    // -------------------------------------------------------------------------
    // Type helpers (used in template and tests)
    // -------------------------------------------------------------------------

    isOcOciType(): boolean {
        return this.selectedType === 'OCOCI';
    }

    isPartNumberType(): boolean {
        return this.selectedType === 'PN';
    }

    isCircuitType(): boolean {
        return this.selectedType === 'CIRCNO';
    }

    // -------------------------------------------------------------------------
    // Validation
    // -------------------------------------------------------------------------

    isFormValid(): boolean {
        if (!this.selectedType) {
            return false;
        }
        if (this.isOcOciType()) {
            if (!this.selectedObjectCode || typeof this.selectedObjectCode !== 'object') {
                return false;
            }
            const oc = this.selectedObjectCode.code;
            return oc != null && String(oc).trim().length > 0;
        }
        if (this.isPartNumberType()) {
            const pn = this.partNumber?.trim() ?? '';
            return pn.length > 0 && pn.length <= 10;
        }
        if (this.isCircuitType()) {
            const cn = this.circuitNumber?.trim() ?? '';
            return cn.length > 0 && cn.length <= 8;
        }
        return false;
    }

    // -------------------------------------------------------------------------
    // Input sanitizers — strip invalid chars and normalize in place.
    // Using (input) handlers rather than pKeyFilter due to PrimeNG v21 reliability issues.
    // -------------------------------------------------------------------------

    onOciInput(event: Event): void {
        const input = event.target as HTMLInputElement;
        const sanitized = input.value.replace(/[^a-zA-Z0-9_%]/g, '').toUpperCase();
        input.value = sanitized;
        this.objectCodeInstance = sanitized;
    }

    onPartNumberInput(event: Event): void {
        const input = event.target as HTMLInputElement;
        const sanitized = input.value.replace(/[^0-9]/g, '');
        input.value = sanitized;
        this.partNumber = sanitized;
    }

    onCircuitNumberInput(event: Event): void {
        const input = event.target as HTMLInputElement;
        const sanitized = input.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
        input.value = sanitized;
        this.circuitNumber = sanitized;
    }

    // -------------------------------------------------------------------------
    // Object Code autocomplete — filters the preloaded list in memory
    // -------------------------------------------------------------------------

    searchObjectCodes(event: { query: string }): void {
        if (!this.permission()) {
            this.filteredObjectCodes = [];
            return;
        }
        if (!this.objectCodesLoaded) {
            this.loadAllObjectCodes();
        }
        const query = (event.query ?? '').trim().toLowerCase();
        if (query.length === 0) {
            this.filteredObjectCodes = [...this.allObjectCodes];
            return;
        }
        this.filteredObjectCodes = this.allObjectCodes.filter(oc =>
            oc.code?.toLowerCase().includes(query) ||
            oc.objectCodeDesc?.toLowerCase().includes(query)
        );
    }

    // -------------------------------------------------------------------------
    // Run / polling / download
    // -------------------------------------------------------------------------

    runReport(): void {
        if (!this.permission() || !this.isFormValid() || this.isRunning) {
            return;
        }

        this.isRunning = true;
        this.messageService.clear();

        const request = this.buildRequest();
        this.esciService.submitCapitalJob(request).subscribe({
            next: (resp: { jobId: string }) => {
                this.startPolling(resp.jobId);
            },
            error: () => {
                this.handleJobError('Failed to submit Capital query. Please try again later.');
            }
        });
    }

    private buildRequest(): any {
        const base: any = {partType: this.selectedType};
        if (this.isOcOciType()) {
            base.objectCode = String(this.selectedObjectCode.code).trim();
            base.objectCodeInstance = this.objectCodeInstance?.trim().toUpperCase() ?? '';
        } else if (this.isPartNumberType()) {
            base.partNumber = this.partNumber?.trim();
        } else if (this.isCircuitType()) {
            base.circuitNumber = this.circuitNumber?.trim();
        }
        return base;
    }

    private startPolling(jobId: string): void {
        const maxMs = this.MAX_POLL_MINUTES * 60 * 1000;
        this.pollingDone = false;

        interval(this.POLL_INTERVAL_MS).pipe(
            takeUntil(this.stopPolling$),
            takeUntil(timer(maxMs)),
            switchMap(() => this.esciService.pollCapitalJobStatus(jobId))
        ).subscribe({
            next: (status: { jobId: string; status: string; message: string }) => {
                if (status.status === 'COMPLETE') {
                    this.pollingDone = true;
                    this.stopPolling$.next();
                    this.downloadResult(jobId);
                } else if (status.status === 'FAILED') {
                    this.pollingDone = true;
                    this.stopPolling$.next();
                    this.handleJobError(
                        status.message?.trim()
                            ? status.message
                            : 'Capital query failed. Please try again later.'
                    );
                }
                // PENDING / IN_PROGRESS: keep polling
            },
            error: () => {
                this.handleJobError('Lost connection while polling Capital status. Please try again.');
            },
            complete: () => {
                // Only fires as a true 20-minute timeout when pollingDone is false.
                // When stopPolling$.next() ends the stream early (COMPLETE or FAILED),
                // pollingDone is already true so this block is skipped.
                if (this.isRunning && !this.pollingDone) {
                    this.handleJobError(
                        `Query timed out after ${this.MAX_POLL_MINUTES} minutes. ` +
                        'The Capital query may still be running in the background. Please contact support if needed.'
                    );
                }
            }
        });
    }

    private downloadResult(jobId: string): void {
        this.downloadService.downloadCapitalJobResult(jobId).subscribe({
            next: (blob: Blob) => {
                const fileName = `Capital_Component_Usage_${CommonHelperService.getTimeStampForReport()}.zip`;
                CommonHelperService.downloadFile(blob, fileName);
                this.messageService.processSuccess('Capital Component Usage report downloaded successfully.');
                this.finalizeJob();
            },
            error: () => {
                this.handleJobError('Failed to download the Capital report. Please try again.');
            }
        });
    }

    private handleJobError(message: string): void {
        this.messageService.processError(message);
        this.finalizeJob();
    }

    private finalizeJob(): void {
        this.isRunning = false;
    }

    // -------------------------------------------------------------------------
    // Lifecycle
    // -------------------------------------------------------------------------

    ngOnDestroy(): void {
        this.stopPolling$.next();
        this.stopPolling$.complete();
    }
}
